import React, { useState, useEffect } from 'react';
import {
  ProductModel,
  VariantSKU,
  SizeCode,
  CartItem,
  Order,
  OrderStatus,
  ExchangeRequest,
  InventoryAuditLog,
} from './types';
import { INITIAL_PRODUCTS, INITIAL_SKUS } from './data/mockProducts';
import { generatePromptPayPayload } from './utils/promptpay';
import { Navbar } from './components/Navbar';
import { Banner } from './components/Banner';
import { ProductCard } from './components/ProductCard';
import {
  X,
  Ruler,
  Check,
  AlertCircle,
  ShoppingBag,
  Truck,
  RotateCcw,
  ShieldCheck,
  QrCode,
  Clock,
  Package,
  CheckCircle2,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  ExternalLink,
  Search,
  Filter,
  RefreshCw,
  Box,
  ClipboardList,
} from 'lucide-react';

export const App: React.FC = () => {
  // State
  const [products] = useState<ProductModel[]>(INITIAL_PRODUCTS);
  const [skus, setSkus] = useState<VariantSKU[]>(() => {
    const saved = localStorage.getItem('tee_store_skus');
    return saved ? JSON.parse(saved) : INITIAL_SKUS;
  });

  const [cart, setCart] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('tee_store_cart');
    return saved ? JSON.parse(saved) : [];
  });

  const [orders, setOrders] = useState<Order[]>(() => {
    const saved = localStorage.getItem('tee_store_orders');
    return saved ? JSON.parse(saved) : [];
  });

  const [exchangeRequests, setExchangeRequests] = useState<ExchangeRequest[]>(() => {
    const saved = localStorage.getItem('tee_store_exchanges');
    return saved ? JSON.parse(saved) : [];
  });

  const [auditLogs, setAuditLogs] = useState<InventoryAuditLog[]>(() => {
    const saved = localStorage.getItem('tee_store_audit_logs');
    return saved ? JSON.parse(saved) : [];
  });

  // UI States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedSizeFilter, setSelectedSizeFilter] = useState<string>('All');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [trackingSearch, setTrackingSearch] = useState('');
  const [activePaymentOrder, setActivePaymentOrder] = useState<Order | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(15 * 60);

  // Product Detail Modal State
  const [selectedProduct, setSelectedProduct] = useState<ProductModel | null>(null);
  const [detailColorIdx, setDetailColorIdx] = useState(0);
  const [detailSize, setDetailSize] = useState<SizeCode>('M');
  const [detailQty, setDetailQty] = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [sizeGuideUnit, setSizeGuideUnit] = useState<'inch' | 'cm'>('inch');

  // Exchange Modal State
  const [exchangeModalOrder, setExchangeModalOrder] = useState<Order | null>(null);
  const [exchangeReason, setExchangeReason] = useState('');
  const [exchangeNewSize, setExchangeNewSize] = useState<SizeCode>('L');
  const [exchangeHasTag, setExchangeHasTag] = useState(true);

  // Sync to LocalStorage
  useEffect(() => {
    localStorage.setItem('tee_store_skus', JSON.stringify(skus));
  }, [skus]);
  useEffect(() => {
    localStorage.setItem('tee_store_cart', JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem('tee_store_orders', JSON.stringify(orders));
  }, [orders]);
  useEffect(() => {
    localStorage.setItem('tee_store_exchanges', JSON.stringify(exchangeRequests));
  }, [exchangeRequests]);
  useEffect(() => {
    localStorage.setItem('tee_store_audit_logs', JSON.stringify(auditLogs));
  }, [auditLogs]);

  // Countdown Timer & Auto-Release Stock on Timeout (BR-02: 15-Minute Stock Holding Window)
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();

      // Check payment modal timer
      if (activePaymentOrder) {
        const expires = new Date(activePaymentOrder.holdExpiresAt).getTime();
        const diff = Math.max(0, Math.floor((expires - now) / 1000));
        setTimeRemaining(diff);

        if (diff <= 0) {
          // Cancel active order and release stock
          handleOrderExpired(activePaymentOrder.id);
          setActivePaymentOrder(null);
        }
      }

      // Check all pending orders for expiration
      orders.forEach((o) => {
        if (o.status === 'PENDING_PAYMENT') {
          const expires = new Date(o.holdExpiresAt).getTime();
          if (now >= expires) {
            handleOrderExpired(o.id);
          }
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [activePaymentOrder, orders]);

  // Release expired stock
  const handleOrderExpired = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order || order.status !== 'PENDING_PAYMENT') return;

    // Return reserved stock
    setSkus((prevSkus) =>
      prevSkus.map((sku) => {
        const item = order.items.find((i) => i.skuId === sku.id);
        if (item) {
          const newReserved = Math.max(0, sku.reservedStock - item.quantity);
          return { ...sku, reservedStock: newReserved };
        }
        return sku;
      })
    );

    // Update order status
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: 'CANCELLED_EXPIRED' } : o))
    );

    // Audit log
    order.items.forEach((item) => {
      const sku = skus.find((s) => s.id === item.skuId);
      if (sku) {
        addAuditLog(sku.skuCode, 'RELEASE_EXPIRY', item.quantity, sku.physicalStock, order.orderNumber);
      }
    });
  };

  const addAuditLog = (
    skuCode: string,
    action: InventoryAuditLog['action'],
    changeQty: number,
    remainingStock: number,
    referenceId: string
  ) => {
    const newLog: InventoryAuditLog = {
      id: 'log-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
      skuCode,
      action,
      changeQty,
      remainingStock,
      referenceId,
      timestamp: new Date().toLocaleTimeString('th-TH'),
    };
    setAuditLogs((prev) => [newLog, ...prev.slice(0, 99)]);
  };

  // Cart Operations
  const handleAddToCart = (
    product: ProductModel,
    colorName: string,
    colorHex: string,
    size: SizeCode,
    quantity: number
  ) => {
    const sku = skus.find(
      (s) => s.productModelId === product.id && s.colorName === colorName && s.sizeCode === size
    );
    if (!sku) return;

    const available = sku.physicalStock - sku.reservedStock;
    if (available <= 0) {
      alert('ขออภัย สินค้านี้หมดชั่วคราว');
      return;
    }

    setCart((prev) => {
      const existing = prev.find((item) => item.skuId === sku.id);
      if (existing) {
        const newQty = Math.min(available, existing.quantity + quantity);
        return prev.map((item) =>
          item.skuId === sku.id ? { ...item, quantity: newQty } : item
        );
      }
      return [
        ...prev,
        {
          id: 'cart-' + Date.now(),
          skuId: sku.id,
          product,
          colorName,
          colorHex,
          size,
          quantity: Math.min(available, quantity),
          price: product.basePrice,
        },
      ];
    });

    setSelectedProduct(null);
    setIsCartOpen(true);
  };

  const handleUpdateCartQty = (cartId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id === cartId) {
            const sku = skus.find((s) => s.id === item.skuId);
            const max = sku ? sku.physicalStock - sku.reservedStock : item.quantity;
            const newQty = item.quantity + delta;
            if (newQty <= 0) return null;
            return { ...item, quantity: Math.min(max, newQty) };
          }
          return item;
        })
        .filter(Boolean) as CartItem[]
    );
  };

  const handleRemoveFromCart = (cartId: string) => {
    setCart((prev) => prev.filter((i) => i.id !== cartId));
  };

  // Calculations
  const cartSubtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartTotalQty = cart.reduce((sum, item) => sum + item.quantity, 0);
  const FREE_SHIPPING_THRESHOLD = 800; // BR-03
  const isFreeShipping = cartSubtotal >= FREE_SHIPPING_THRESHOLD;
  const shippingFee = cart.length === 0 ? 0 : isFreeShipping ? 0 : 50;
  const cartTotalAmount = cartSubtotal + shippingFee;

  // Checkout & Hold Stock (Atomic Reservation Flow)
  const handleConfirmCheckout = (customerData: Order['customer']) => {
    // Validate stock for all items
    for (const item of cart) {
      const sku = skus.find((s) => s.id === item.skuId);
      if (!sku || (sku.physicalStock - sku.reservedStock) < item.quantity) {
        alert(`ขออภัย สินค้า ${item.product.name} ไซส์ ${item.size} เหลือไม่เพียงพอ`);
        return;
      }
    }

    const orderNumber = 'TS' + Math.floor(100000 + Math.random() * 900000);
    const holdExpiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();

    // 1. Hold stock in SKUs
    setSkus((prev) =>
      prev.map((sku) => {
        const cartItem = cart.find((i) => i.skuId === sku.id);
        if (cartItem) {
          const newReserved = sku.reservedStock + cartItem.quantity;
          addAuditLog(sku.skuCode, 'HOLD', cartItem.quantity, sku.physicalStock - newReserved, orderNumber);
          return { ...sku, reservedStock: newReserved };
        }
        return sku;
      })
    );

    // 2. Create Order
    const newOrder: Order = {
      id: 'ord-' + Date.now(),
      orderNumber,
      customer: customerData,
      items: cart.map((i) => ({
        skuId: i.skuId,
        productName: i.product.name,
        colorName: i.colorName,
        size: i.size,
        quantity: i.quantity,
        price: i.price,
        image: i.product.colors.find((c) => c.name === i.colorName)?.image || i.product.colors[0].image,
      })),
      subtotal: cartSubtotal,
      shippingFee,
      discount: 0,
      totalAmount: cartTotalAmount,
      status: 'PENDING_PAYMENT',
      createdAt: new Date().toISOString(),
      holdExpiresAt,
    };

    setOrders((prev) => [newOrder, ...prev]);
    setCart([]);
    setIsCheckoutOpen(false);
    setIsCartOpen(false);
    setActivePaymentOrder(newOrder);
    setTimeRemaining(15 * 60);
  };

  // Simulate Payment Webhook Callback (PromptPay QR Paid)
  const handleSimulatePaymentSuccess = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;

    // Deduct stock permanently from physical stock and release hold
    setSkus((prev) =>
      prev.map((sku) => {
        const item = order.items.find((i) => i.skuId === sku.id);
        if (item) {
          const newPhysical = Math.max(0, sku.physicalStock - item.quantity);
          const newReserved = Math.max(0, sku.reservedStock - item.quantity);
          addAuditLog(sku.skuCode, 'DEDUCT', -item.quantity, newPhysical, order.orderNumber);
          return { ...sku, physicalStock: newPhysical, reservedStock: newReserved };
        }
        return sku;
      })
    );

    // Update order status
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: 'PAID', paidAt: new Date().toISOString() } : o))
    );

    setActivePaymentOrder(null);
    alert(`🎉 ได้รับยอดชำระเงิน ฿${order.totalAmount.toLocaleString()} เรียบร้อยแล้ว! ออร์เดอร์ ${order.orderNumber} เข้าสู่กระบวนการเตรียมพัสดุ`);
    setIsTrackingOpen(true);
    setTrackingSearch(order.orderNumber);
  };

  // Filter products
  const filteredProducts = products.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.colors.some((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchCategory = selectedCategory === 'All' || p.category === selectedCategory;

    const matchSize =
      selectedSizeFilter === 'All' ||
      skus.some(
        (s) =>
          s.productModelId === p.id &&
          s.sizeCode === selectedSizeFilter &&
          s.physicalStock - s.reservedStock > 0
      );

    return matchSearch && matchCategory && matchSize;
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* Top Banner */}
      <Banner />

      {/* Main Navbar */}
      <Navbar
        cartCount={cartTotalQty}
        onOpenCart={() => setIsCartOpen(true)}
        onOpenTracking={() => setIsTrackingOpen(true)}
        isAdmin={isAdmin}
        onToggleAdmin={() => setIsAdmin(!isAdmin)}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Admin Mode View */}
      {isAdmin ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
          <div className="bg-purple-900 text-white rounded-3xl p-6 sm:p-8 mb-8 shadow-xl flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-purple-300 bg-purple-800/80 px-3 py-1 rounded-full">
                Admin Operations Portal
              </span>
              <h1 className="text-2xl sm:text-3xl font-bold mt-2">ศูนย์บริหารจัดการร้านค้า & คลังสต็อก</h1>
              <p className="text-sm text-purple-200 mt-1">
                ควบคุมสต็อกระดับ SKU, จัดการคำสั่งซื้อ, ตรวจสอบ Audit Log และคำร้องขอเปลี่ยนไซส์เสื้อ
              </p>
            </div>
            <button
              onClick={() => setIsAdmin(false)}
              className="bg-white text-purple-950 font-bold px-5 py-2.5 rounded-xl text-sm hover:bg-purple-50 shadow-md"
            >
              กลับสู่หน้าร้านค้า
            </button>
          </div>

          {/* Admin Tabs / Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Column 1 & 2: Inventory SKU Matrix & Orders */}
            <div className="lg:col-span-2 space-y-8">
              {/* SKU Inventory Table */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Box className="w-5 h-5 text-purple-600" />
                    <h2 className="font-bold text-gray-900 text-lg">รายการสต็อกแยกตาม SKU (Variant Matrix)</h2>
                  </div>
                  <span className="text-xs bg-purple-50 text-purple-700 font-semibold px-2.5 py-1 rounded-lg">
                    ทั้งหมด {skus.length} SKUs
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider border-y border-gray-100">
                      <tr>
                        <th className="py-2.5 px-3">รหัส SKU</th>
                        <th className="py-2.5 px-3">สี</th>
                        <th className="py-2.5 px-3">ไซส์</th>
                        <th className="py-2.5 px-3 text-center">สต็อกจริง</th>
                        <th className="py-2.5 px-3 text-center">กำลังจอง</th>
                        <th className="py-2.5 px-3 text-center">พร้อมขาย</th>
                        <th className="py-2.5 px-3 text-right">ปรับสต็อก</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {skus.map((sku) => {
                        const available = sku.physicalStock - sku.reservedStock;
                        const isLow = available < 5;
                        return (
                          <tr key={sku.id} className="hover:bg-gray-50/80">
                            <td className="py-2.5 px-3 font-mono font-semibold text-gray-900">{sku.skuCode}</td>
                            <td className="py-2.5 px-3 text-gray-600">{sku.colorName}</td>
                            <td className="py-2.5 px-3 font-bold text-gray-800">{sku.sizeCode}</td>
                            <td className="py-2.5 px-3 text-center font-medium">{sku.physicalStock}</td>
                            <td className="py-2.5 px-3 text-center text-amber-600 font-medium">
                              {sku.reservedStock > 0 ? sku.reservedStock : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full font-bold text-[11px] ${
                                  available <= 0
                                    ? 'bg-red-100 text-red-700'
                                    : isLow
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {available}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-right">
                              <div className="inline-flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    setSkus((prev) =>
                                      prev.map((s) =>
                                        s.id === sku.id
                                          ? { ...s, physicalStock: s.physicalStock + 5 }
                                          : s
                                      )
                                    );
                                    addAuditLog(sku.skuCode, 'RESTOCK', 5, sku.physicalStock + 5, 'ADMIN-MANUAL');
                                  }}
                                  className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-[11px] font-semibold"
                                >
                                  +5 เติมของ
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Orders Management */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-purple-600" />
                    <h2 className="font-bold text-gray-900 text-lg">จัดการคำสั่งซื้อ (Orders Management)</h2>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-700 font-semibold px-2.5 py-1 rounded-lg">
                    {orders.length} รายการ
                  </span>
                </div>

                {orders.length === 0 ? (
                  <p className="text-xs text-gray-400 py-6 text-center">ยังไม่มีคำสั่งซื้อในระบบ</p>
                ) : (
                  <div className="space-y-3">
                    {orders.map((o) => (
                      <div
                        key={o.id}
                        className="p-4 rounded-xl border border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3 text-xs"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-gray-900">#{o.orderNumber}</span>
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                o.status === 'PAID'
                                  ? 'bg-blue-100 text-blue-800'
                                  : o.status === 'SHIPPED'
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : o.status === 'DELIVERED'
                                  ? 'bg-green-100 text-green-800'
                                  : o.status === 'CANCELLED_EXPIRED'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {o.status}
                            </span>
                          </div>
                          <div className="text-gray-500 mt-1">
                            ลูกค้า: {o.customer.fullName} ({o.customer.phone}) • ยอด ฿{o.totalAmount.toLocaleString()}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {o.status === 'PAID' && (
                            <button
                              onClick={() => {
                                const tracking = 'TH' + Math.floor(100000000 + Math.random() * 900000000);
                                setOrders((prev) =>
                                  prev.map((ord) =>
                                    ord.id === o.id
                                      ? {
                                          ...ord,
                                          status: 'SHIPPED',
                                          carrier: 'Flash Express',
                                          trackingNumber: tracking,
                                          shippedAt: new Date().toISOString(),
                                        }
                                      : ord
                                  )
                                );
                              }}
                              className="px-3 py-1.5 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700"
                            >
                              ออกเลขพัสดุ & จัดส่ง
                            </button>
                          )}
                          {o.status === 'SHIPPED' && (
                            <button
                              onClick={() => {
                                setOrders((prev) =>
                                  prev.map((ord) =>
                                    ord.id === o.id
                                      ? {
                                          ...ord,
                                          status: 'DELIVERED',
                                          deliveredAt: new Date().toISOString(),
                                        }
                                      : ord
                                  )
                                );
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700"
                            >
                              จำลองส่งถึงปลายทาง (Delivered)
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Column 3: Real-Time Audit Log & Exchanges */}
            <div className="space-y-8">
              {/* Inventory Audit Logs (NFR-06) */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-purple-600" />
                    <h3 className="font-bold text-gray-900 text-sm">บันทึกสต็อกเรียลไทม์ (Audit Logs)</h3>
                  </div>
                  <span className="text-[10px] text-gray-400">Append-Only</span>
                </div>
                <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                  {auditLogs.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">ยังไม่มีประวัติการเคลื่อนไหวสต็อก</p>
                  ) : (
                    auditLogs.map((log) => (
                      <div
                        key={log.id}
                        className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 text-[11px] font-mono flex items-center justify-between"
                      >
                        <div>
                          <div className="font-bold text-gray-800">{log.skuCode}</div>
                          <div className="text-gray-400 text-[10px]">{log.referenceId} • {log.timestamp}</div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`font-bold ${
                              log.changeQty > 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {log.action}: {log.changeQty > 0 ? `+${log.changeQty}` : log.changeQty}
                          </span>
                          <div className="text-gray-400 text-[10px]">คงเหลือ: {log.remainingStock}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Exchange Requests */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-gray-900 text-sm mb-3">คำร้องขอเปลี่ยนไซส์เสื้อ (Exchanges)</h3>
                {exchangeRequests.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">ไม่มีคำร้องขอเปลี่ยนไซส์</p>
                ) : (
                  <div className="space-y-2">
                    {exchangeRequests.map((ex) => (
                      <div key={ex.id} className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-xs">
                        <div className="flex justify-between font-bold text-gray-800">
                          <span>ออร์เดอร์ #{ex.orderNumber}</span>
                          <span className="text-purple-600">{ex.status}</span>
                        </div>
                        <p className="text-gray-600 mt-1">ขอเปลี่ยนเป็นไซส์: <strong>{ex.requestedSize}</strong></p>
                        <p className="text-gray-400 text-[11px]">เหตุผล: {ex.reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      ) : (
        /* Storefront View */
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
          {/* Hero Section */}
          <div className="relative rounded-3xl bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden p-8 sm:p-12 mb-10 shadow-2xl">
            <div className="relative z-10 max-w-2xl">
              <span className="inline-block bg-green-500/20 text-green-400 border border-green-500/30 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
                ✨ New Season 2026 Collection
              </span>
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
                เสื้อยืดคอตตอนแท้ 100% ทรงสวย อยู่ทรง ไม่ย้วย
              </h1>
              <p className="mt-4 text-sm sm:text-base text-gray-300 leading-relaxed">
                ออกแบบด้วยความประณีต ผ้านุ่มใส่สบาย ระบายความร้อนดีเยี่ยม พร้อมตารางวัดสัดส่วนที่แม่นยำ หมดปัญหาเรื่องไซส์ไม่พอดี
              </p>
              <div className="mt-6 flex flex-wrap gap-4 text-xs font-semibold">
                <span className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3.5 py-2 rounded-xl">
                  <Check className="w-4 h-4 text-green-400" /> จัดส่งฟรีเมื่อครบ 800.-
                </span>
                <span className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3.5 py-2 rounded-xl">
                  <Check className="w-4 h-4 text-green-400" /> รองรับสแกนจ่าย QR
                </span>
                <span className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3.5 py-2 rounded-xl">
                  <Check className="w-4 h-4 text-green-400" /> คืน/เปลี่ยนไซส์ใน 7 วัน
                </span>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b border-gray-200">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {['All', 'Oversized', 'Vintage', 'Sport', 'Basic'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                    selectedCategory === cat
                      ? 'bg-gray-900 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {cat === 'All' ? 'ทั้งหมด' : cat}
                </button>
              ))}
            </div>

            {/* In-Stock Size Filter (FR-03) */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">กรองไซส์ที่มีของ:</span>
              <div className="flex gap-1">
                {['All', 'S', 'M', 'L', 'XL', '2XL'].map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedSizeFilter(s)}
                    className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${
                      selectedSizeFilter === s
                        ? 'bg-green-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                skus={skus}
                onSelectProduct={(p, idx) => {
                  setSelectedProduct(p);
                  setDetailColorIdx(idx || 0);
                  setDetailSize(p.sizes[0]);
                  setDetailQty(1);
                  setShowSizeGuide(false);
                }}
              />
            ))}
          </div>
        </main>
      )}

      {/* Cart Drawer Slide-over */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end">
          <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-green-600" />
                <h2 className="font-bold text-gray-900 text-lg">ตะกร้าสินค้า ({cartTotalQty})</h2>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Free Shipping Progress (BR-03) */}
            <div className="p-4 bg-green-50 border-b border-green-100">
              <div className="flex items-center justify-between text-xs mb-1.5 font-semibold">
                <span className="text-green-800 flex items-center gap-1">
                  <Truck className="w-3.5 h-3.5" />
                  {isFreeShipping ? 'คุณได้รับสิทธิ์จัดส่งฟรีแล้ว! 🎉' : `ซื้อเพิ่มอีก ฿${(FREE_SHIPPING_THRESHOLD - cartSubtotal).toLocaleString()} เพื่อรับสิทธิ์ส่งฟรี`}
                </span>
                <span className="text-green-700">{Math.min(100, Math.round((cartSubtotal / FREE_SHIPPING_THRESHOLD) * 100))}%</span>
              </div>
              <div className="w-full h-1.5 bg-green-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-600 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, (cartSubtotal / FREE_SHIPPING_THRESHOLD) * 100)}%` }}
                />
              </div>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">ตะกร้าของคุณยังว่างอยู่</p>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.id} className="flex gap-3.5 p-3 rounded-2xl border border-gray-100 bg-gray-50/50">
                    <img
                      src={item.product.colors.find((c) => c.name === item.colorName)?.image || item.product.colors[0].image}
                      alt={item.product.name}
                      className="w-16 h-20 object-cover rounded-xl bg-gray-200"
                    />
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-start">
                          <h4 className="font-semibold text-gray-900 text-xs line-clamp-1">{item.product.name}</h4>
                          <button
                            onClick={() => handleRemoveFromCart(item.id)}
                            className="text-gray-400 hover:text-red-500 p-0.5"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: item.colorHex }} />
                          {item.colorName} • ไซส์ <strong>{item.size}</strong>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <span className="font-bold text-gray-900 text-sm">฿{(item.price * item.quantity).toLocaleString()}</span>
                        <div className="flex items-center border border-gray-200 rounded-lg bg-white overflow-hidden text-xs">
                          <button
                            onClick={() => handleUpdateCartQty(item.id, -1)}
                            className="px-2 py-1 text-gray-500 hover:bg-gray-100"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="px-2.5 font-bold text-gray-800">{item.quantity}</span>
                          <button
                            onClick={() => handleUpdateCartQty(item.id, 1)}
                            className="px-2 py-1 text-gray-500 hover:bg-gray-100"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer Summary & Checkout */}
            {cart.length > 0 && (
              <div className="p-4 sm:p-6 border-t border-gray-100 bg-white space-y-3">
                <div className="space-y-1.5 text-xs text-gray-600">
                  <div className="flex justify-between">
                    <span>ยอดรวมสินค้า</span>
                    <span>฿{cartSubtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ค่าจัดส่ง</span>
                    <span>{isFreeShipping ? <strong className="text-green-600">จัดส่งฟรี</strong> : '฿50'}</span>
                  </div>
                  <div className="flex justify-between text-base font-extrabold text-gray-900 pt-2 border-t border-gray-100">
                    <span>ยอดรวมสุทธิ</span>
                    <span>฿{cartTotalAmount.toLocaleString()}</span>
                  </div>
                </div>

                <button
                  onClick={() => setIsCheckoutOpen(true)}
                  className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-2xl shadow-lg shadow-green-600/30 flex items-center justify-center gap-2 text-sm"
                >
                  ดำเนินการชำระเงิน <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Modal (Hold Stock 15 Mins) */}
      {isCheckoutOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative">
            <button
              onClick={() => setIsCheckoutOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1">ข้อมูลจัดส่งพัสดุ</h2>
            <p className="text-xs text-gray-500 mb-4">
              กรอกที่อยู่สำหรับจัดส่ง เมื่อกดยืนยัน ระบบจะ Hold สต็อกไว้ให้คุณเป็นเวลา 15 นาที
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.target as HTMLFormElement;
                const customerData: Order['customer'] = {
                  fullName: (form.elements.namedItem('fullName') as HTMLInputElement).value,
                  phone: (form.elements.namedItem('phone') as HTMLInputElement).value,
                  address: (form.elements.namedItem('address') as HTMLInputElement).value,
                  subdistrict: (form.elements.namedItem('subdistrict') as HTMLInputElement).value,
                  district: (form.elements.namedItem('district') as HTMLInputElement).value,
                  province: (form.elements.namedItem('province') as HTMLInputElement).value,
                  postalCode: (form.elements.namedItem('postalCode') as HTMLInputElement).value,
                };
                handleConfirmCheckout(customerData);
              }}
              className="space-y-3 text-xs"
            >
              <div>
                <label className="block text-gray-700 font-semibold mb-1">ชื่อ-นามสกุล ผู้รับ *</label>
                <input required name="fullName" defaultValue="สมชาย ใจดี" className="w-full p-2.5 border rounded-xl" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-1">เบอร์โทรศัพท์ (สำหรับขนส่ง) *</label>
                <input required name="phone" defaultValue="0812345678" className="w-full p-2.5 border rounded-xl" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-1">ที่อยู่ (บ้านเลขที่, หมู่, ถนน, ซอย) *</label>
                <input required name="address" defaultValue="99/123 หมู่บ้านสุขสบาย ซอย 5" className="w-full p-2.5 border rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">แขวง/ตำบล *</label>
                  <input required name="subdistrict" defaultValue="บางนา" className="w-full p-2.5 border rounded-xl" />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">เขต/อำเภอ *</label>
                  <input required name="district" defaultValue="บางนา" className="w-full p-2.5 border rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">จังหวัด *</label>
                  <input required name="province" defaultValue="กรุงเทพมหานคร" className="w-full p-2.5 border rounded-xl" />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">รหัสไปรษณีย์ *</label>
                  <input required name="postalCode" defaultValue="10260" className="w-full p-2.5 border rounded-xl" />
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-sm font-bold text-gray-900">
                <span>ยอดชำระเงินทั้งสิ้น</span>
                <span>฿{cartTotalAmount.toLocaleString()}</span>
              </div>

              <button
                type="submit"
                className="w-full mt-2 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-lg text-sm flex items-center justify-center gap-2"
              >
                ยืนยันคำสั่งซื้อ & รับ QR ชำระเงิน
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Dynamic PromptPay QR Payment Modal (FR-06 & BR-02) */}
      {activePaymentOrder && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 sm:p-7 shadow-2xl relative text-center">
            {/* Header / Timer */}
            <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-amber-600 bg-amber-50 py-1.5 px-3 rounded-full mb-3 inline-flex">
              <Clock className="w-3.5 h-3.5" />
              <span>
                กำลัง Hold สต็อกไว้ให้: {Math.floor(timeRemaining / 60)}:{('0' + (timeRemaining % 60)).slice(-2)} นาที
              </span>
            </div>

            <h3 className="font-extrabold text-lg text-gray-900">สแกนจ่ายผ่าน Mobile Banking</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              ออร์เดอร์ #{activePaymentOrder.orderNumber}
            </p>

            {/* PromptPay QR Container */}
            <div className="mt-4 p-5 bg-gradient-to-b from-blue-900 to-indigo-950 rounded-2xl text-white shadow-lg">
              <div className="flex items-center justify-between pb-3 border-b border-white/10 text-xs">
                <span className="font-bold tracking-wider">PROMPTPAY</span>
                <span className="text-[10px] text-gray-300">พร้อมเพย์</span>
              </div>

              <div className="bg-white p-3 rounded-xl my-3 inline-block shadow-inner">
                {/* Simulated QR Code Visual */}
                <div className="w-48 h-48 bg-gray-100 flex flex-col items-center justify-center border-4 border-gray-900 p-2 relative">
                  <QrCode className="w-36 h-36 text-gray-900" />
                  <span className="text-[8px] font-mono text-gray-500 absolute bottom-1">
                    {activePaymentOrder.orderNumber} • ฿{activePaymentOrder.totalAmount}
                  </span>
                </div>
              </div>

              <div className="text-xs font-medium text-gray-300">
                ยอดชำระ: <strong className="text-white text-base">฿{activePaymentOrder.totalAmount.toLocaleString()}</strong>
              </div>
            </div>

            <div className="mt-3 text-[11px] text-gray-400">
              *เมื่อชำระเงินแล้ว ระบบจะตัดสต็อกและยืนยันออร์เดอร์อัตโนมัติ
            </div>

            {/* Simulation Button for Webhook Confirmation */}
            <div className="mt-4 space-y-2">
              <button
                onClick={() => handleSimulatePaymentSuccess(activePaymentOrder.id)}
                className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-md shadow-green-600/30"
              >
                <CheckCircle2 className="w-4 h-4" /> จำลองลูกค้าชำระเงินสำเร็จ (Test Webhook)
              </button>
              <button
                onClick={() => {
                  handleOrderExpired(activePaymentOrder.id);
                  setActivePaymentOrder(null);
                }}
                className="text-xs text-gray-400 hover:text-red-500"
              >
                ยกเลิกคำสั่งซื้อและปล่อยสต็อกคืน
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Tracking Modal (FR-08 & FR-09) */}
      {isTrackingOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsTrackingOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1">ติดตามสถานะคำสั่งซื้อ & พัสดุ</h2>
            <p className="text-xs text-gray-500 mb-4">
              กรอกหมายเลขคำสั่งซื้อ (เช่น TS123456) หรือเบอร์โทรศัพท์
            </p>

            <div className="flex gap-2 mb-6">
              <input
                type="text"
                placeholder="ระบุเลขคำสั่งซื้อ เช่น TS123456"
                value={trackingSearch}
                onChange={(e) => setTrackingSearch(e.target.value)}
                className="flex-1 p-2.5 border rounded-xl text-xs"
              />
            </div>

            {/* Matching Orders */}
            <div className="space-y-4">
              {orders
                .filter(
                  (o) =>
                    !trackingSearch ||
                    o.orderNumber.toLowerCase().includes(trackingSearch.toLowerCase()) ||
                    o.customer.phone.includes(trackingSearch)
                )
                .slice(0, 5)
                .map((o) => (
                  <div key={o.id} className="p-4 rounded-2xl border border-gray-100 bg-gray-50 text-xs">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <span className="font-mono font-bold text-gray-900">#{o.orderNumber}</span>
                        <span className="text-gray-400 ml-2">{new Date(o.createdAt).toLocaleDateString('th-TH')}</span>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded font-bold text-[10px] ${
                          o.status === 'PAID'
                            ? 'bg-blue-100 text-blue-800'
                            : o.status === 'SHIPPED'
                            ? 'bg-indigo-100 text-indigo-800'
                            : o.status === 'DELIVERED'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {o.status}
                      </span>
                    </div>

                    {/* Timeline */}
                    <div className="grid grid-cols-4 gap-1 text-[10px] text-center my-3 text-gray-500">
                      <div className={`p-1.5 rounded ${o.status !== 'CANCELLED_EXPIRED' ? 'bg-green-100 text-green-800 font-bold' : ''}`}>
                        1. รับออร์เดอร์
                      </div>
                      <div className={`p-1.5 rounded ${['PAID', 'PREPARING', 'SHIPPED', 'DELIVERED'].includes(o.status) ? 'bg-green-100 text-green-800 font-bold' : ''}`}>
                        2. ชำระเงินแล้ว
                      </div>
                      <div className={`p-1.5 rounded ${['SHIPPED', 'DELIVERED'].includes(o.status) ? 'bg-green-100 text-green-800 font-bold' : ''}`}>
                        3. กำลังจัดส่ง
                      </div>
                      <div className={`p-1.5 rounded ${o.status === 'DELIVERED' ? 'bg-green-100 text-green-800 font-bold' : ''}`}>
                        4. ได้รับสินค้า
                      </div>
                    </div>

                    {/* Tracking No */}
                    {o.trackingNumber && (
                      <div className="p-2.5 bg-indigo-50 border border-indigo-100 rounded-xl mb-3 flex items-center justify-between">
                        <div>
                          <div className="text-[10px] text-indigo-500 font-medium">ขนส่ง: {o.carrier}</div>
                          <div className="font-mono font-bold text-indigo-950 text-xs">เลขพัสดุ: {o.trackingNumber}</div>
                        </div>
                        <span className="text-[10px] text-indigo-700 bg-white px-2 py-1 rounded shadow-xs font-semibold">
                          ตรวจสอบสถานะ ↗
                        </span>
                      </div>
                    )}

                    {/* Size Exchange Request Button (FR-09) */}
                    {o.status === 'DELIVERED' && (
                      <button
                        onClick={() => setExchangeModalOrder(o)}
                        className="w-full mt-2 py-2 border border-green-600 text-green-700 hover:bg-green-50 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> ยื่นขอเปลี่ยนไซส์เสื้อ (ภายใน 7 วัน)
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Size Exchange Modal (FR-09 & BR-04) */}
      {exchangeModalOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl relative text-xs">
            <button
              onClick={() => setExchangeModalOrder(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-bold text-base text-gray-900 mb-1">
              ยื่นคำร้องขอเปลี่ยนไซส์เสื้อ (ออร์เดอร์ #{exchangeModalOrder.orderNumber})
            </h3>
            <p className="text-gray-500 mb-4">
              ตามเงื่อนไขทางร้าน เสื้อต้องไม่ผ่านการซักและยังมีป้ายสินค้า (Hangtag) ติดอยู่ครบถ้วน
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const newEx: ExchangeRequest = {
                  id: 'ex-' + Date.now(),
                  orderId: exchangeModalOrder.id,
                  orderNumber: exchangeModalOrder.orderNumber,
                  customerName: exchangeModalOrder.customer.fullName,
                  phone: exchangeModalOrder.customer.phone,
                  originalSku: exchangeModalOrder.items[0]?.skuId || '',
                  requestedSize: exchangeNewSize,
                  reason: exchangeReason,
                  hasTag: exchangeHasTag,
                  status: 'PENDING',
                  createdAt: new Date().toISOString(),
                };
                setExchangeRequests((prev) => [newEx, ...prev]);
                setExchangeModalOrder(null);
                alert('✅ ส่งคำร้องขอเปลี่ยนไซส์เรียบร้อยแล้ว! เจ้าหน้าที่จะติดต่อกลับภายใน 24 ชม.');
              }}
              className="space-y-3"
            >
              <div>
                <label className="font-semibold block mb-1">เลือกขนาดใหม่ที่ต้องการเปลี่ยน:</label>
                <div className="flex gap-2">
                  {(['S', 'M', 'L', 'XL', '2XL'] as SizeCode[]).map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setExchangeNewSize(s)}
                      className={`w-9 h-9 rounded-xl font-bold text-xs ${
                        exchangeNewSize === s
                          ? 'bg-green-600 text-white'
                          : 'border text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-semibold block mb-1">ระบุเหตุผลในการขอเปลี่ยน:</label>
                <input
                  required
                  placeholder="เช่น สั่งไซส์ M มาแล้วฟิตเกินไป ต้องการเปลี่ยนเป็นไซส์ L"
                  value={exchangeReason}
                  onChange={(e) => setExchangeReason(e.target.value)}
                  className="w-full p-2.5 border rounded-xl text-xs"
                />
              </div>

              <div className="p-3 bg-gray-50 rounded-xl border flex items-center gap-2">
                <input
                  type="checkbox"
                  id="tagCheck"
                  checked={exchangeHasTag}
                  onChange={(e) => setExchangeHasTag(e.target.checked)}
                  required
                  className="w-4 h-4 text-green-600 rounded"
                />
                <label htmlFor="tagCheck" className="text-gray-700">
                  ข้าพเจ้ายืนยันว่าเสื้อผ้ายังไม่ผ่านการซัก และยังมีป้ายสินค้าครบถ้วน
                </label>
              </div>

              <button
                type="submit"
                className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl"
              >
                ส่งคำร้อง
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
          <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row my-auto max-h-[92vh]">
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-white/90 shadow-md flex items-center justify-center text-gray-500 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Left Image */}
            <div className="md:w-1/2 bg-gray-100 relative flex items-center justify-center min-h-[300px] md:min-h-[480px]">
              <img
                src={selectedProduct.colors[detailColorIdx]?.image || selectedProduct.colors[0].image}
                alt={selectedProduct.name}
                className="w-full h-full object-cover object-center max-h-[400px] md:max-h-full"
              />
              <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ backgroundColor: selectedProduct.colors[detailColorIdx]?.hex }}
                />
                {selectedProduct.colors[detailColorIdx]?.name}
              </div>
            </div>

            {/* Right Details */}
            <div className="md:w-1/2 p-6 sm:p-8 overflow-y-auto flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-green-700 bg-green-50 px-2 py-0.5 rounded">
                    {selectedProduct.category}
                  </span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
                  {selectedProduct.name}
                </h2>
                <div className="text-2xl font-extrabold text-gray-900 mt-2">
                  ฿{selectedProduct.basePrice.toLocaleString()}
                </div>

                <p className="text-xs sm:text-sm text-gray-600 mt-3 leading-relaxed">
                  {selectedProduct.description}
                </p>

                <div className="mt-3 p-3 bg-gray-50 rounded-xl text-xs text-gray-500 border border-gray-100">
                  <strong className="text-gray-700">เนื้อผ้า:</strong> {selectedProduct.fabricDetails}
                </div>

                {/* Color Selection */}
                <div className="mt-5">
                  <div className="text-xs font-bold text-gray-700 mb-2">
                    1. เลือกสี: {selectedProduct.colors[detailColorIdx]?.name}
                  </div>
                  <div className="flex gap-2">
                    {selectedProduct.colors.map((c, idx) => (
                      <button
                        key={c.name}
                        onClick={() => setDetailColorIdx(idx)}
                        className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                          detailColorIdx === idx
                            ? 'ring-2 ring-green-600 ring-offset-2 scale-110'
                            : 'border-gray-300 hover:scale-105'
                        }`}
                        style={{ backgroundColor: c.hex }}
                        title={c.name}
                      >
                        {detailColorIdx === idx && (
                          <Check className={`w-4 h-4 ${c.hex === '#FFFFFF' ? 'text-black' : 'text-white'}`} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Size Selection + Size Guide Modal */}
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-700">2. เลือกขนาดเสื้อ (Size)</span>
                    <button
                      type="button"
                      onClick={() => setShowSizeGuide(!showSizeGuide)}
                      className="flex items-center gap-1 text-xs text-green-700 font-semibold underline"
                    >
                      <Ruler className="w-3.5 h-3.5" /> ตารางเทียบขนาด (Size Guide)
                    </button>
                  </div>

                  <div className="grid grid-cols-5 gap-2">
                    {selectedProduct.sizes.map((s) => {
                      const activeColor = selectedProduct.colors[detailColorIdx];
                      const sku = skus.find(
                        (skuItem) =>
                          skuItem.productModelId === selectedProduct.id &&
                          skuItem.colorName === activeColor.name &&
                          skuItem.sizeCode === s
                      );
                      const stockLeft = sku ? sku.physicalStock - sku.reservedStock : 0;
                      const isSoldOut = stockLeft <= 0;

                      return (
                        <button
                          key={s}
                          disabled={isSoldOut}
                          onClick={() => setDetailSize(s)}
                          className={`py-2 px-1 rounded-xl border text-sm font-semibold flex flex-col items-center justify-center ${
                            isSoldOut
                              ? 'bg-gray-100 text-gray-300 line-through cursor-not-allowed'
                              : detailSize === s
                              ? 'border-green-600 bg-green-50 text-green-800 ring-1 ring-green-600'
                              : 'border-gray-200 text-gray-800 hover:border-gray-300 bg-white'
                          }`}
                        >
                          {s}
                          {!isSoldOut && <span className="text-[10px] text-gray-400">เหลือ {stockLeft}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Size Guide Table Toggle */}
                {showSizeGuide && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-2xl border border-green-200 text-xs animate-in slide-in-from-top">
                    <div className="flex justify-between items-center mb-2 font-bold">
                      <span>ตารางวัดสัดส่วน ({selectedProduct.name})</span>
                      <div className="flex bg-gray-200 p-0.5 rounded-lg text-[11px]">
                        <button
                          onClick={() => setSizeGuideUnit('inch')}
                          className={`px-2 py-0.5 rounded ${sizeGuideUnit === 'inch' ? 'bg-white shadow-xs font-bold' : ''}`}
                        >
                          นิ้ว
                        </button>
                        <button
                          onClick={() => setSizeGuideUnit('cm')}
                          className={`px-2 py-0.5 rounded ${sizeGuideUnit === 'cm' ? 'bg-white shadow-xs font-bold' : ''}`}
                        >
                          ซม.
                        </button>
                      </div>
                    </div>
                    <table className="w-full text-center">
                      <thead className="text-gray-400 border-b">
                        <tr>
                          <th className="py-1 text-left">ไซส์</th>
                          <th>รอบอก</th>
                          <th>ยาว</th>
                          <th>ไหล่</th>
                          <th>แขน</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedProduct.sizes.map((sz) => {
                          const m = selectedProduct.sizeChart[sz];
                          if (!m) return null;
                          const factor = sizeGuideUnit === 'cm' ? 2.54 : 1;
                          return (
                            <tr key={sz} className={detailSize === sz ? 'bg-green-100/70 font-bold' : ''}>
                              <td className="py-1 text-left font-bold">{sz}</td>
                              <td>{(m.chest * factor).toFixed(sizeGuideUnit === 'cm' ? 1 : 0)}</td>
                              <td>{(m.length * factor).toFixed(sizeGuideUnit === 'cm' ? 1 : 0)}</td>
                              <td>{(m.shoulder * factor).toFixed(sizeGuideUnit === 'cm' ? 1 : 0)}</td>
                              <td>{(m.sleeve * factor).toFixed(sizeGuideUnit === 'cm' ? 1 : 0)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Quantity */}
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700">จำนวน</span>
                  <div className="flex items-center border rounded-xl overflow-hidden">
                    <button
                      onClick={() => setDetailQty(Math.max(1, detailQty - 1))}
                      className="px-3 py-1 bg-gray-50 hover:bg-gray-100 font-bold"
                    >
                      -
                    </button>
                    <span className="px-4 py-1 text-xs font-bold">{detailQty}</span>
                    <button
                      onClick={() => setDetailQty(detailQty + 1)}
                      className="px-3 py-1 bg-gray-50 hover:bg-gray-100 font-bold"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Add to Cart Button */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    const activeColor = selectedProduct.colors[detailColorIdx];
                    handleAddToCart(selectedProduct, activeColor.name, activeColor.hex, detailSize, detailQty);
                  }}
                  className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-2xl shadow-lg flex items-center justify-center gap-2 text-sm"
                >
                  <ShoppingBag className="w-4 h-4" />
                  เพิ่มลงตะกร้า • ฿{(selectedProduct.basePrice * detailQty).toLocaleString()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 text-xs py-10 mt-auto border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-4">
          <div>
            <span className="text-white font-bold text-sm">TEE STORE ONLINE</span>
            <p className="text-gray-500 mt-1">ระบบขายเสื้อผ้าออนไลน์เฟสที่ 1 • Deploy to Cloudflare Pages</p>
          </div>
          <div className="flex gap-4">
            <span className="hover:text-white cursor-pointer" onClick={() => setIsTrackingOpen(true)}>ติดตามพัสดุ</span>
            <span className="hover:text-white cursor-pointer" onClick={() => setIsAdmin(!isAdmin)}>Admin Portal</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
